import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';

import { CircularProgress } from '../../../components/circular-progress/circular-progress';
import { calculatePercentage, getDone } from '../../../lib/utils/math';
import { ProjectContacts } from './project-contacts';
import { EditCoverButton } from './edit-cover.button';
import { Pulse } from '../../../components/page-loader/PageLoader';
import { formatPicture } from '../../../core/services/image.service';
import { Project } from '../../../app/entities';
import { TimeFrame } from './timeframe';
import { fromLocalDate, toLegacyInstant, type PlainDate } from '../../../core/services/plain-date';

import styles from './project-overview-header.module.scss';
interface ProjectOverviewHeaderProps {
  project: Project;
}

export const ProjectOverviewHeader: React.FC<ProjectOverviewHeaderProps> = observer(
  ({ project }) => {
    //TODO: move [sum, done, progress] to project entity
    const { sum, done } = getDone(project.stages);
    const progress = calculatePercentage(done, sum);
    const [isLoading, setIsLoading] = useState(false);

    const handleChangeCover = async (file: File) => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      const options = { width: 640, height: 380 };
      const frame = await formatPicture(file, options);

      await project.updateCover(frame);

      setIsLoading(false);
    };

    // `Project` is NOT migrated to `PlainDate` — it keeps instant semantics and has its own
    // follow-up ticket, and its API path does `new Date(input.startDate)`, which reads a bare
    // `YYYY-MM-DD` as server-zone midnight. So the picker's calendar day has to go back on the
    // wire as the legacy local-noon instant. `null` (the clear action) is forwarded untouched.
    const handleUpdateDate = (values: {
      dueDate?: PlainDate | null;
      startDate?: PlainDate | null;
    }) => {
      const update: Record<string, string | null> = {};

      if (values.startDate !== undefined) {
        update.startDate = values.startDate && toLegacyInstant(values.startDate);
      }

      if (values.dueDate !== undefined) {
        update.dueDate = values.dueDate && toLegacyInstant(values.dueDate);
      }

      project.update(update);
    };

    return (
      <Header>
        <Cover>
          <HeaderImage
            src={project.avatar || '/images/project_cover_placeholder.jpg'}
            alt={project.name}
          />
          <EditCoverButton onChange={handleChangeCover} />

          {isLoading && (
            <CoverLoading>
              <PulseWrapper>
                <Pulse />
              </PulseWrapper>
            </CoverLoading>
          )}
        </Cover>

        <Right>
          <h1>{project.name}</h1>
          <Client>{project.client?.name}</Client>

          <Infos>
            <InfoItem>
              <IconContainer>
                <CircularProgress progress={progress} />
              </IconContainer>

              <Text>
                Steps completed:&nbsp;
                <b>
                  {done}/{sum}
                </b>
              </Text>
            </InfoItem>

            <InfoItem>
              <TimeFrame
                dueDate={project.dueDate ? fromLocalDate(new Date(project.dueDate)) : undefined}
                startDate={
                  project.startDate ? fromLocalDate(new Date(project.startDate)) : undefined
                }
                onUpdate={handleUpdateDate}
              />
            </InfoItem>

            <InfoItem></InfoItem>
          </Infos>
        </Right>

        <ProjectContacts
          projectId={project._id}
          spaceId={project.spaceId}
          contacts={project.contacts || []}
        />
      </Header>
    );
  },
);

const Header = ({ className, ...props }: React.ComponentPropsWithoutRef<'header'>) => (
  <header
    className={className ? `${styles['header']} ${className}` : styles['header']}
    {...props}
  />
);

const Cover = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['cover']} ${className}` : styles['cover']} {...props} />
);

const CoverLoading = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['cover-loading']} ${className}` : styles['cover-loading']}
    {...props}
  />
);

const PulseWrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['pulse-wrapper']} ${className}` : styles['pulse-wrapper']}
    {...props}
  />
);

const HeaderImage = ({ className, ...props }: React.ComponentPropsWithoutRef<'img'>) => (
  <img
    className={className ? `${styles['header-image']} ${className}` : styles['header-image']}
    {...props}
  />
);

export const Right = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['right']} ${className}` : styles['right']} {...props} />
);

export const Client = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['client']} ${className}` : styles['client']} {...props} />
);

const Infos = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['infos']} ${className}` : styles['infos']} {...props} />
);

export const InfoItem = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['info-item']} ${className}` : styles['info-item']}
    {...props}
  />
);

export const Text = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['text']} ${className}` : styles['text']} {...props} />
);

const IconContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['icon-container']} ${className}` : styles['icon-container']}
    {...props}
  />
);
